/**
 * スキルマップテーブルの幅計算ユーティリティ
 */

import { ProductLargeCategory, ProductMediumCategory, ProductSmallCategory, HeaderCellWidths } from './types';
import { WIDTH_CONSTRAINTS } from './constants';

/**
 * 細項目の幅を拡張する
 * @param adjustedWidths 調整中の幅配列
 * @param startIndex 開始インデックス
 * @param count 細項目の数
 * @param additionalWidth 追加が必要な幅
 * @param maxWidth 最大幅
 */
const expandDetailWidths = (
  adjustedWidths: number[],
  startIndex: number,
  count: number,
  additionalWidth: number,
  maxWidth: number
): void => {
  const widthPerChild = Math.ceil(additionalWidth / count);

  for (let i = 0; i < count; i++) {
    const childIndex = startIndex + i;
    const currentWidth = adjustedWidths[childIndex];
    const newWidth = Math.min(currentWidth + widthPerChild, maxWidth);
    adjustedWidths[childIndex] = newWidth;
  }
};

/**
 * 指定範囲の幅の合計を計算する
 * @param adjustedWidths 幅配列
 * @param startIndex 開始インデックス
 * @param count 項目数
 * @returns 合計幅
 */
const calculateTotalWidth = (
  adjustedWidths: number[],
  startIndex: number,
  count: number
): number => {
  let total = 0;
  for (let i = 0; i < count; i++) {
    total += adjustedWidths[startIndex + i];
  }
  return total;
};

/**
 * 小項目とその配下の細項目の幅を計算・調整する
 * @param smallCategory 小項目
 * @param adjustedWidths 調整中の幅配列
 * @param detailIndex 現在の細項目インデックス（後の拡張性を鑑み、参照渡しとする）
 * @param measureTextWidth テキスト幅測定関数
 * @param maxWidth 最大幅
 * @returns 小項目の幅
 */
const calculateSmallCategoryWidth = (
  smallCategory: ProductSmallCategory,
  adjustedWidths: number[],
  detailIndex: { current: number },
  measureTextWidth: (text: string) => number,
  maxWidth: number
): number => {
  const smallCategoryTextWidth = measureTextWidth(smallCategory.product_small_category_name || "");
  const smallStartIndex = detailIndex.current;
  const detailCategoryCount = smallCategory.product_detail_categories.length;

  // 細項目の幅の合計を計算
  let smallCategoryWidth = calculateTotalWidth(
    adjustedWidths,
    detailIndex.current,
    detailCategoryCount
  );
  detailIndex.current += detailCategoryCount;

  // 小項目のテキストが細項目の合計より大きい場合、細項目を拡張
  if (smallCategoryTextWidth > smallCategoryWidth && detailCategoryCount > 0) {
    const additionalWidthNeeded = smallCategoryTextWidth - smallCategoryWidth;
    expandDetailWidths(
      adjustedWidths,
      smallStartIndex,
      detailCategoryCount,
      additionalWidthNeeded,
      maxWidth
    );

    // 小項目の幅を再計算
    smallCategoryWidth = calculateTotalWidth(
      adjustedWidths,
      smallStartIndex,
      detailCategoryCount
    );
  }

  return smallCategoryWidth;
};

/**
 * 配下の小項目の幅を再計算する
 * @param mediumCategory 中項目
 * @param adjustedWidths 調整後の幅配列
 * @param mediumStartIndex 中項目の開始インデックス
 * @param smallCategoryWidths 小項目の幅配列（更新対象）
 * @param baseSmallIndex 小項目配列の基準インデックス
 */
const recalculateSmallWidths = (
  mediumCategory: ProductMediumCategory,
  adjustedWidths: number[],
  mediumStartIndex: number,
  smallCategoryWidths: number[],
  baseSmallIndex: number
): void => {
  let smallDetailIndex = mediumStartIndex;
  let smallIndex = 0;

  mediumCategory.product_small_categories.forEach((smallCategory: ProductSmallCategory) => {
    const detailCount = smallCategory.product_detail_categories.length;
    const newSmallWidth = calculateTotalWidth(
      adjustedWidths,
      smallDetailIndex,
      detailCount
    );
    smallCategoryWidths[baseSmallIndex + smallIndex] = newSmallWidth;
    smallDetailIndex += detailCount;
    smallIndex++;
  });
};

/**
 * 中項目とその配下の小項目の幅を計算・調整する
 * @param mediumCategory 中項目
 * @param adjustedWidths 調整中の幅配列
 * @param detailIndex 現在の細項目インデックス
 * @param smallCategoryWidths 小項目の幅配列
 * @param measureTextWidth テキスト幅測定関数
 * @param maxWidth 最大幅
 * @returns 中項目の幅
 */
const calculateMediumCategoryWidth = (
  mediumCategory: ProductMediumCategory,
  adjustedWidths: number[],
  detailIndex: { current: number },
  smallCategoryWidths: number[],
  measureTextWidth: (text: string) => number,
  maxWidth: number
): number => {
  const mediumCategoryTextWidth = measureTextWidth(mediumCategory.product_medium_category_name || "");
  const mediumStartIndex = detailIndex.current;

  // 配下の小項目を処理
  mediumCategory.product_small_categories.forEach((smallCategory: ProductSmallCategory) => {
    const smallWidth = calculateSmallCategoryWidth(
      smallCategory,
      adjustedWidths,
      detailIndex,
      measureTextWidth,
      maxWidth
    );
    smallCategoryWidths.push(smallWidth);
  });

  // 中項目: 配下の全細項目の合計幅を計算
  const mediumDetailCount = detailIndex.current - mediumStartIndex;
  let mediumCategoryWidth = calculateTotalWidth(
    adjustedWidths,
    mediumStartIndex,
    mediumDetailCount
  );

  // 中項目のテキストが小項目の合計より大きい場合、細項目を拡張
  if (mediumCategoryTextWidth > mediumCategoryWidth && mediumDetailCount > 0) {
    const additionalWidthNeeded = mediumCategoryTextWidth - mediumCategoryWidth;
    expandDetailWidths(
      adjustedWidths,
      mediumStartIndex,
      mediumDetailCount,
      additionalWidthNeeded,
      maxWidth
    );

    // 中項目の幅を再計算
    mediumCategoryWidth = calculateTotalWidth(
      adjustedWidths,
      mediumStartIndex,
      mediumDetailCount
    );

    // 小項目の幅も再計算
    const baseSmallIndex = smallCategoryWidths.length - mediumCategory.product_small_categories.length;
    recalculateSmallWidths(
      mediumCategory,
      adjustedWidths,
      mediumStartIndex,
      smallCategoryWidths,
      baseSmallIndex
    );
  }

  return mediumCategoryWidth;
};

/**
 * 配下の中項目と小項目の幅を再計算する
 * @param largeCategory 大項目
 * @param adjustedWidths 調整後の幅配列
 * @param largeStartIndex 大項目の開始インデックス
 * @param mediumCategoryWidths 中項目の幅配列（更新対象）
 * @param smallCategoryWidths 小項目の幅配列（更新対象）
 */
const recalculateMediumAndSmallWidths = (
  largeCategory: ProductLargeCategory,
  adjustedWidths: number[],
  largeStartIndex: number,
  mediumCategoryWidths: number[],
  smallCategoryWidths: number[]
): void => {
  let mediumDetailIndex = largeStartIndex;
  let mediumIndex = 0;
  const baseMediumIndex = mediumCategoryWidths.length - largeCategory.product_medium_categories.length;

  largeCategory.product_medium_categories.forEach((mediumCategory: ProductMediumCategory) => {
    const mediumDetailCount = mediumCategory.product_small_categories.reduce(
      (sum: number, small: ProductSmallCategory) => sum + small.product_detail_categories.length,
      0
    );
    const newMediumWidth = calculateTotalWidth(
      adjustedWidths,
      mediumDetailIndex,
      mediumDetailCount
    );
    mediumCategoryWidths[baseMediumIndex + mediumIndex] = newMediumWidth;

    // 小項目の幅も再計算
    const totalSmallCount = largeCategory.product_medium_categories.reduce(
      (sum: number, m: ProductMediumCategory) => sum + m.product_small_categories.length,
      0
    );
    const baseSmallIndex = smallCategoryWidths.length - totalSmallCount;

    let smallDetailIndex = mediumDetailIndex;
    let smallIndex = 0;
    mediumCategory.product_small_categories.forEach((smallCategory: ProductSmallCategory) => {
      const detailCount = smallCategory.product_detail_categories.length;
      const newSmallWidth = calculateTotalWidth(
        adjustedWidths,
        smallDetailIndex,
        detailCount
      );
      smallCategoryWidths[baseSmallIndex + smallIndex] = newSmallWidth;
      smallDetailIndex += detailCount;
      smallIndex++;
    });

    mediumDetailIndex += mediumDetailCount;
    mediumIndex++;
  });
};

/**
 * 大項目とその配下の中項目・小項目の幅を計算・調整する
 * @param largeCategory 大項目
 * @param adjustedWidths 調整中の幅配列
 * @param detailIndex 現在の細項目インデックス
 * @param mediumCategoryWidths 中項目の幅配列
 * @param smallCategoryWidths 小項目の幅配列
 * @param measureTextWidth テキスト幅測定関数
 * @param maxWidth 最大幅
 * @returns 大項目の幅
 */
const calculateLargeCategoryWidth = (
  largeCategory: ProductLargeCategory,
  adjustedWidths: number[],
  detailIndex: { current: number },
  mediumCategoryWidths: number[],
  smallCategoryWidths: number[],
  measureTextWidth: (text: string) => number,
  maxWidth: number
): number => {
  const largeCategoryTextWidth = measureTextWidth(largeCategory.product_large_category_name || "");
  const largeStartIndex = detailIndex.current;

  // 配下の中項目を処理
  largeCategory.product_medium_categories.forEach((mediumCategory: ProductMediumCategory) => {
    const mediumWidth = calculateMediumCategoryWidth(
      mediumCategory,
      adjustedWidths,
      detailIndex,
      smallCategoryWidths,
      measureTextWidth,
      maxWidth
    );
    mediumCategoryWidths.push(mediumWidth);
  });

  // 大項目: 配下の全細項目の合計幅を計算
  const largeDetailCount = detailIndex.current - largeStartIndex;
  let largeCategoryWidth = calculateTotalWidth(
    adjustedWidths,
    largeStartIndex,
    largeDetailCount
  );

  // 大項目のテキストが中項目の合計より大きい場合、細項目を拡張
  if (largeCategoryTextWidth > largeCategoryWidth && largeDetailCount > 0) {
    const additionalWidthNeeded = largeCategoryTextWidth - largeCategoryWidth;
    expandDetailWidths(
      adjustedWidths,
      largeStartIndex,
      largeDetailCount,
      additionalWidthNeeded,
      maxWidth
    );

    // 大項目の幅を再計算
    largeCategoryWidth = calculateTotalWidth(
      adjustedWidths,
      largeStartIndex,
      largeDetailCount
    );

    // 中項目と小項目の幅も再計算
    recalculateMediumAndSmallWidths(
      largeCategory,
      adjustedWidths,
      largeStartIndex,
      mediumCategoryWidths,
      smallCategoryWidths
    );
  }

  return largeCategoryWidth;
};

/**
 * ヘッダーセルの幅を計算する
 * @param header ヘッダーデータ（大項目の配列）
 * @param dataColumnWidths 細項目の初期幅配列
 * @param measureTextWidth テキスト幅測定関数
 * @returns 各階層の幅と調整後の細項目幅
 */
export const calculateHeaderCellWidths = (
  header: ProductLargeCategory[],
  dataColumnWidths: number[],
  measureTextWidth: (text: string) => number
): HeaderCellWidths => {
  if (!dataColumnWidths.length) {
    return {
      large: [],
      medium: [],
      small: [],
      adjustedDataColumnWidths: [...dataColumnWidths]
    };
  }

  const maxWidth = WIDTH_CONSTRAINTS.DATA_COLUMN.MAX;
  const adjustedDataColumnWidths = [...dataColumnWidths];
  const largeCategoryWidths: number[] = [];
  const mediumCategoryWidths: number[] = [];
  const smallCategoryWidths: number[] = [];

  const detailIndex = { current: 0 };

  // 各大項目を処理
  header.forEach((largeCategory) => {
    const largeWidth = calculateLargeCategoryWidth(
      largeCategory,
      adjustedDataColumnWidths,
      detailIndex,
      mediumCategoryWidths,
      smallCategoryWidths,
      measureTextWidth,
      maxWidth
    );
    largeCategoryWidths.push(largeWidth);
  });

  return {
    large: largeCategoryWidths,
    medium: mediumCategoryWidths,
    small: smallCategoryWidths,
    adjustedDataColumnWidths
  };
};
